/** Alpha Vantage (API key): TIME_SERIES_DAILY, GLOBAL_QUOTE, SYMBOL_SEARCH. */
import { z } from 'zod';
import { KeelError } from '../errors.js';
import { diffDays, fromUTC } from '../dates.js';
import { assertDateRange, codeFromSymbol, currencyForSymbol, exchangeForSymbol, getJson, IsoDateString, normaliseCurrency, NumericString, parseOrThrow, priceToCents, requireKey, } from './provider.js';
const ID = 'alphavantage';
const BASE = 'https://www.alphavantage.co/query';
const DailySchema = z.object({
    'Time Series (Daily)': z.record(IsoDateString, z.object({
        '1. open': NumericString, '2. high': NumericString, '3. low': NumericString, '4. close': NumericString, '5. volume': NumericString,
    })),
});
const QuoteSchema = z.object({
    'Global Quote': z.object({ '01. symbol': z.string(), '05. price': NumericString, '07. latest trading day': IsoDateString }).partial(),
});
const SearchSchema = z.object({
    bestMatches: z.array(z.object({ '1. symbol': z.string(), '2. name': z.string(), '3. type': z.string(), '8. currency': z.string() })).prefault([]),
});
const Envelope = z.record(z.string(), z.unknown());
/** Calls the API; Alpha Vantage reports errors and rate limits as HTTP 200 with Note / Information / Error Message. */
const call = async (ctx, params) => {
    const key = requireKey(ctx, ID);
    const body = await getJson(ctx, ID, `${BASE}?${params}&apikey=${encodeURIComponent(key)}`);
    const env = Envelope.safeParse(body);
    if (env.success) {
        for (const k of ['Error Message', 'Note', 'Information']) {
            if (k in env.data)
                throw new KeelError('PROVIDER', `${ID}: ${String(env.data[k])}`, { kind: k });
        }
    }
    return body;
};
const securityType = (t) => {
    switch (t.toLowerCase()) {
        case 'equity': return 'share';
        case 'etf': return 'etf';
        case 'mutual fund': return 'managed_fund';
        default: return 'other';
    }
};
export const createAlphaVantageProvider = (ctx) => ({
    id: ID,
    name: 'Alpha Vantage',
    requiresApiKey: true,
    async history(symbol, from, to) {
        assertDateRange(ID, from, to);
        const size = diffDays(from, fromUTC(ctx.now())) > 100 ? 'full' : 'compact'; // compact = last 100 bars
        const body = await call(ctx, `function=TIME_SERIES_DAILY&symbol=${encodeURIComponent(symbol)}&outputsize=${size}`);
        const series = parseOrThrow(DailySchema, body, ID)['Time Series (Daily)'];
        const ccy = currencyForSymbol(ID, symbol) ?? 'USD';
        const c = (v) => priceToCents(v, ccy).cents;
        return Object.keys(series).filter((d) => d >= from && d <= to).sort().map((date) => {
            const b = series[date];
            return {
                date, closeCents: c(b['4. close']), open: c(b['1. open']), high: c(b['2. high']), low: c(b['3. low']),
                volume: Number(b['5. volume']), source: ID,
            };
        });
    },
    async quote(symbols) {
        const out = [];
        for (const symbol of symbols) { // sequential: free tier is rate-limited per minute
            const q = parseOrThrow(QuoteSchema, await call(ctx, `function=GLOBAL_QUOTE&symbol=${encodeURIComponent(symbol)}`), ID)['Global Quote'];
            if (!q['05. price'])
                continue; // unknown symbol → empty object
            const { cents, currency } = priceToCents(q['05. price'], currencyForSymbol(ID, symbol) ?? 'USD');
            out.push({ symbol, priceCents: cents, currency, asOf: `${q['07. latest trading day'] ?? fromUTC(ctx.now())}T00:00:00.000Z` });
        }
        return out;
    },
    async search(q, opts = {}) {
        const { bestMatches } = parseOrThrow(SearchSchema, await call(ctx, `function=SYMBOL_SEARCH&keywords=${encodeURIComponent(q)}`), ID);
        return bestMatches.slice(0, opts.limit ?? 10).map((m) => ({
            symbol: m['1. symbol'],
            code: codeFromSymbol(m['1. symbol']),
            exchange: exchangeForSymbol(ID, m['1. symbol']) ?? '',
            name: m['2. name'],
            type: securityType(m['3. type']),
            currency: normaliseCurrency(m['8. currency']),
        }));
    },
});
//# sourceMappingURL=alphavantage.js.map